import React, { useState, useRef, useEffect } from 'react';
import { Card, Typography, Row, Col, Segmented, Tag, Button, Grid, Popover } from 'antd';
import { LineOutlined, CarOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const AI_INSIGHT_POPOVER_CONTENT = {
  buyer_demand: {
    intro: "Measures how reliably buyers purchase this vehicle when priced correctly.",
    signalsUsed: ["Units sold in last 60-90 days", "Median sold days on market", "Sell-through vs active inventory"],
    gradeMeaning: [
      { grades: "A–B", desc: "Strong demand" },
      { grades: "C", desc: "Selective demand" },
      { grades: "D–F", desc: "Weak or inconsistent" },
    ],
  },
  interest: {
    intro: "Measures how actively buyers are engaging with similar listings in your market.",
    signalsUsed: ["Active listing freshness", "Sold velocity", "Price alignment", "Momentum"],
    gradeMeaning: [
      { grades: "A–B", desc: "High interest" },
      { grades: "C", desc: "Moderate interest" },
      { grades: "D–F", desc: "Low interest" },
    ],
  },
  volume: {
    intro: "Measures how much real market activity exists to support reliable insights.",
    signalsUsed: ["Sold activity in window", "Active pool size", "Market liquidity"],
    gradeMeaning: [
      { grades: "A–B", desc: "High volume" },
      { grades: "C", desc: "Moderate volume" },
      { grades: "D–F", desc: "Low volume" },
    ],
  },
  availability: {
    intro: "Measures how competitive and accessible this vehicle is for independent dealers.",
    signalsUsed: ["Active-to-sold ratio", "Days supply", "Franchise vs independent mix", "Stale inventory"],
    gradeMeaning: [
      { grades: "A–B", desc: "Good availability" },
      { grades: "C", desc: "Tight availability" },
      { grades: "D–F", desc: "Limited availability" },
    ],
  },
  mds: {
    intro: "Measures how challenging it is to source, price, and turn this vehicle profitably.",
    signalsUsed: ["Days supply", "Crowding ratio", "Franchise/independent mix", "Price volatility"],
    gradeMeaning: [
      { grades: "A–B", desc: "Easy to sell" },
      { grades: "C", desc: "Moderate" },
      { grades: "D–F", desc: "Hard or very hard" },
    ],
  },
  market_friction: {
    intro: "Measures how smooth or frustrating it is to price, manage, and exit this vehicle.",
    signalsUsed: ["Price dispersion", "DOM predictability", "Version fragmentation", "Long-tail inventory"],
    gradeMeaning: [
      { grades: "A–B", desc: "Low friction" },
      { grades: "C", desc: "Moderate friction" },
      { grades: "D–F", desc: "High friction" },
    ],
  },
};

function InsightPopoverContent({ label, content }) {
  if (!content) return null;
  const popoverStyle = {
    background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
    borderRadius: 10,
    padding: "14px 16px",
    maxWidth: 320,
    border: "1px solid rgba(255,255,255,.06)",
    boxShadow: "0 10px 26px rgba(0,0,0,.35)",
  };
  const titleStyle = { color: "#ffffff", fontSize: 14, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 };
  const pStyle = { color: "#94a3b8", fontSize: 12, lineHeight: 1.5, marginBottom: 12 };
  const headingStyle = { color: "#ffffff", fontSize: 12, fontWeight: 600, marginBottom: 6 };
  const listStyle = { margin: 0, paddingLeft: 18, color: "#94a3b8", fontSize: 12, lineHeight: 1.6 };
  const bulletStyle = { position: "absolute", left: -10, top: 5, width: 5, height: 5, borderRadius: "50%", background: "#f59e0b" };
  const gradeItemStyle = { marginBottom: 4, color: "#94a3b8", fontSize: 12 };
  const gradeBoldStyle = { color: "#ffffff", fontWeight: 600 };
  return (
    <div style={popoverStyle}>
      <div style={titleStyle}>
        {label}
        <span style={{ color: "#64748b", fontSize: 12 }}>ⓘ</span>
      </div>
      <p style={pStyle}>{content.intro}</p>
      <div style={headingStyle}>Signals used:</div>
      <ul style={listStyle}>
        {content.signalsUsed.map((s, i) => (
          <li key={i} style={{ position: "relative", marginBottom: 4 }}>
            <span style={bulletStyle} />
            {s}
          </li>
        ))}
      </ul>
      <div style={{ ...headingStyle, marginTop: 12 }}>Grade meaning:</div>
      <ul style={{ ...listStyle, listStyle: "none", paddingLeft: 0 }}>
        {content.gradeMeaning.map((g, i) => (
          <li key={i} style={gradeItemStyle}>
            <span style={gradeBoldStyle}>{g.grades}</span> {g.desc}
          </li>
        ))}
      </ul>
    </div>
  );
}

const Overview = ({ data, activeTab, hideMarketStrategyInsights = false }) => {
  if (!data) return null;
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  const [chartFilter, setChartFilter] = useState('Active');
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [hoveredPosition, setHoveredPosition] = useState({ x: 0, y: 0 });
  const [showSimilarMileageOnly, setShowSimilarMileageOnly] = useState(false);
  const [isVersionMatchOnly, setIsVersionMatchOnly] = useState(false);
  const similarMileageButtonRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);
  
  // Force button styles when state changes
  useEffect(() => {
    if (similarMileageButtonRef.current) {
      const button = similarMileageButtonRef.current;
      if (showSimilarMileageOnly) {
        button.style.setProperty('background', 'linear-gradient(to right, #a855f7 0%, #22c55e 100%)', 'important');
        button.style.setProperty('border', '1px solid #3b82f6', 'important');
        button.style.setProperty('border-color', '#3b82f6', 'important');
        button.style.setProperty('color', '#ffffff', 'important');
      } else {
        button.style.setProperty('background', 'rgba(255, 255, 255, 0.05)', 'important');
        button.style.setProperty('border', '1px solid rgba(255, 255, 255, 0.1)', 'important');
        button.style.setProperty('border-color', 'rgba(255, 255, 255, 0.1)', 'important');
        button.style.setProperty('color', '#ffffff', 'important');
      }
    }
  }, [showSimilarMileageOnly]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Reset similar mileage and version filters when chart filter changes
  useEffect(() => {
    setShowSimilarMileageOnly(false);
    setIsVersionMatchOnly(false);
  }, [chartFilter]);

  const { vehicle_info, market_analysis, pricing_info, search_metadata } = data;

  const aiMarketInsights = market_analysis?.ai_market_insights ?? data?.ai_market_insights ?? {};
  const AI_INSIGHT_CONFIG = [
    { key: "buyer_demand", label: "Buyer Demand", scoreKey: "demand_score", gradeKey: "demand_grade" },
    { key: "interest", label: "Buyer Interest", scoreKey: "interest_score", gradeKey: "interest_grade" },
    { key: "volume", label: "Volume", scoreKey: "volume_score", gradeKey: "volume_grade" },
    { key: "availability", label: "Availability", scoreKey: "availability_score", gradeKey: "availability_grade" },
    { key: "mds", label: "MDS", scoreKey: "mds_score", gradeKey: "mds_grade" },
    { key: "market_friction", label: "Market Friction", scoreKey: "market_friction_score", gradeKey: "market_friction_grade" },
  ];
  const gradeToColor = (grade) => {
    const g = String(grade ?? "").trim().toUpperCase();
    if (g.startsWith("A") || g.startsWith("B")) return { border: "#22c55e", glow: "rgba(34,197,94,0.5)", text: "#22c55e" };
    if (g.startsWith("C")) return { border: "#eab308", glow: "rgba(234,179,8,0.45)", text: "#eab308" };
    if (g.startsWith("D") || g === "F") return { border: "#ef4444", glow: "rgba(239,68,68,0.5)", text: "#ef4444" };
    return { border: "#94a3b8", glow: "rgba(148,163,184,0.4)", text: "#94a3b8" };
  };
  const formatReasonCode = (code) => (code || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  
  // Get version from vehicle
  const vehicleVersion = vehicle_info?.version || vehicle_info?.trim || null;
  
  // Calculate new active listings from active_listings where first_seen_at_date is within 14 days
  const calculateNewActiveListings = () => {
    const activeListings = market_analysis?.active_listings || [];
    if (activeListings.length === 0) return 0;
    
    // Use search_metadata or current date as reference (new API uses search_timestamp)
    const referenceDate = (search_metadata?.fetched_at || search_metadata?.search_timestamp)
      ? new Date(search_metadata.fetched_at || search_metadata.search_timestamp)
      : new Date();
    referenceDate.setHours(0, 0, 0, 0);
    
    const fourteenDaysAgo = new Date(referenceDate);
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    
    return activeListings.filter(listing => {
      // First try to use first_seen_at_days if available
      if (listing.first_seen_at_days != null && listing.first_seen_at_days !== undefined) {
        return listing.first_seen_at_days <= 14;
      }
      
      // Otherwise calculate from first_seen_at_date
      if (!listing.first_seen_at_date) return false;
      const firstSeenDate = new Date(listing.first_seen_at_date);
      firstSeenDate.setHours(0, 0, 0, 0);
      return firstSeenDate >= fourteenDaysAgo && firstSeenDate <= referenceDate;
    }).length;
  };
  
  const newActiveListings = calculateNewActiveListings();
  
  // Calculate new sold listings from recent_sales where last_seen_at_date is within 14 days
  const calculateNewSoldListings = () => {
    const soldListings = market_analysis?.recent_sales || [];
    if (soldListings.length === 0) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fourteenDaysAgo = new Date(today);
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    
    return soldListings.filter(listing => {
      if (!listing.last_seen_at_date) return false;
      const lastSeenDate = new Date(listing.last_seen_at_date);
      lastSeenDate.setHours(0, 0, 0, 0);
      return lastSeenDate >= fourteenDaysAgo && lastSeenDate <= today;
    }).length;
  };
  
  const newSoldListings = calculateNewSoldListings();

  // Reset similar mileage filter when new search data arrives
  useEffect(() => {
    setShowSimilarMileageOnly(false);
  }, [data?.vehicle_info?.vin, data?.search_metadata?.vin]);

  // Reset similar mileage filter when Overview tab becomes active
  useEffect(() => {
    if (activeTab === 'overview') {
      setShowSimilarMileageOnly(false);
    }
  }, [activeTab]);
  const market_metrics = market_analysis?.market_metrics;
  const recommendation = market_analysis?.recommendation;

  // Real data from API response
  const realData = {
    hooptiePrice: pricing_info?.marketcheck_price || pricing_info?.hooptie_price || null,
    marketAvgPrice: market_metrics?.market_avg_price ? Math.round(market_metrics.market_avg_price) : null,
    retailCeiling: pricing_info?.retail_ceiling || 12306,
    avgDOM: market_metrics?.avg_days_on_market != null ? Math.round(market_metrics.avg_days_on_market) : null,
    soldCount: market_metrics?.sold_count || 8,
    activeCount: market_metrics?.active_count || 6,
    recommendation: recommendation?.action || recommendation?.recommendation || 'BUY',
    confidence: pricing_info?.confidence || 'low'
  };

  // Real scatter plot data from API response
  const soldData = market_analysis?.recent_sales?.map(sale => {
    // Handle different image formats: single image, photos array, or null
    let image = null;
    if (sale.image) image = sale.image;
    else if (sale.photo) image = sale.photo;
    else if (sale.thumbnail) image = sale.thumbnail;
    else if (Array.isArray(sale.photos) && sale.photos.length > 0) image = sale.photos[0];
    else if (Array.isArray(sale.imageUrls) && sale.imageUrls.length > 0) image = sale.imageUrls[0];
    
    return {
      price: sale.price || sale.sold_price,
      mileage: sale.miles || sale.mileage,
      type: 'sold',
      year: sale.year || vehicle_info?.year,
      make: sale.make || vehicle_info?.make,
      model: sale.model || vehicle_info?.model,
      version: sale.version || sale.trim,
      trim: sale.trim,
      heading: sale.heading,
      dom: sale.dom_active,
      vin: sale.vin,
      image: image,
      url: sale.vdp_url || sale.url || sale.listing_url || null
    };
  }).filter(item => item.price && item.mileage) || [];
  
  const activeData = market_analysis?.active_listings?.map(listing => {
    // Handle different image formats: single image, photos array, or null
    let image = null;
    if (listing.image) image = listing.image;
    else if (listing.photo) image = listing.photo;
    else if (listing.thumbnail) image = listing.thumbnail;
    else if (Array.isArray(listing.photos) && listing.photos.length > 0) image = listing.photos[0];
    else if (Array.isArray(listing.imageUrls) && listing.imageUrls.length > 0) image = listing.imageUrls[0];
    
    return {
      price: listing.price,
      mileage: listing.miles,
      type: 'active',
      year: listing.year || vehicle_info?.year,
      make: listing.make || vehicle_info?.make,
      model: listing.model || vehicle_info?.model,
      version: listing.version || listing.trim,
      trim: listing.trim,
      heading: listing.heading,
      dom: listing.dom_active,
      vin: listing.vin,
      image: image,
      url: listing.vdp_url || listing.url || listing.listing_url || null
    };
  }).filter(item => item.price && item.mileage) || [];
  
  // Filter data based on selected filter
  let scatterData = [];
  if (chartFilter === 'Active') {
    scatterData = activeData;
  } else if (chartFilter === 'Sold') {
    scatterData = soldData;
  } else if (chartFilter === 'Both') {
    scatterData = [...soldData, ...activeData];
  }
  
  // Filter out invalid data
  scatterData = scatterData.filter(item => item.price > 0 && item.mileage > 0);
  
  // Filter by similar mileage if enabled
  if (showSimilarMileageOnly) {
    // Try to get vehicle mileage from multiple possible fields
    const vehicleMileage = vehicle_info?.miles || 
                          vehicle_info?.mileage || 
                          search_metadata?.mileage || 
                          search_metadata?.miles;
    
    if (vehicleMileage && typeof vehicleMileage === 'number' && vehicleMileage > 0) {
      const mileageTolerance = vehicleMileage * 0.1; // 10% tolerance
      scatterData = scatterData.filter(item => {
        const mileageDiff = Math.abs(item.mileage - vehicleMileage);
        return mileageDiff <= mileageTolerance;
      });
    }
  }
  
  // Filter by version match if enabled - exact match on version field only
  if (isVersionMatchOnly && vehicleVersion) {
    const vehicleVersionLower = vehicleVersion.toLowerCase().trim();
    scatterData = scatterData.filter(item => {
      const listingVersion = item.version?.toLowerCase().trim();
      // Match only if version field matches exactly
      return listingVersion && listingVersion === vehicleVersionLower;
    });
  }

  // Demand Chart Component - Weekly Sold Count
  const DemandChart = ({ data, isVersionMatchOnly, vehicleVersion }) => {
    let soldListings = data?.market_analysis?.recent_sales || [];
    
    // Always filter by version match if vehicleVersion is available
    if (vehicleVersion) {
      const vehicleVersionLower = vehicleVersion.toLowerCase().trim();
      soldListings = soldListings.filter(listing => {
        const listingVersion = listing.version?.toLowerCase().trim();
        // Match only if version field matches exactly
        return listingVersion && listingVersion === vehicleVersionLower;
      });
    }
    
    // Get the start of the week (Sunday) for a given date
    const getWeekStart = (date) => {
      const weekStart = new Date(date);
      const day = weekStart.getDay();
      weekStart.setDate(weekStart.getDate() - day);
      weekStart.setHours(0, 0, 0, 0);
      return weekStart;
    };
    
    // Generate the past 4 weeks from today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weeks = [];
    for (let i = 3; i >= 0; i--) {
      const weekDate = new Date(today);
      weekDate.setDate(weekDate.getDate() - (i * 7));
      const weekStart = getWeekStart(weekDate);
      weeks.push({
        date: weekStart.toISOString().split('T')[0],
        count: 0
      });
    }
    
    // Count sold listings for each week
    soldListings.forEach(listing => {
      if (listing.last_seen_at_date) {
        const soldDate = new Date(listing.last_seen_at_date);
        soldDate.setHours(0, 0, 0, 0);
        const weekStart = getWeekStart(soldDate);
        const weekKey = weekStart.toISOString().split('T')[0];
        
        // Find the matching week and increment count
        const weekIndex = weeks.findIndex(w => w.date === weekKey);
        if (weekIndex !== -1) {
          weeks[weekIndex].count++;
        }
      }
    });
    
    const weeklyArray = weeks;
    
    // Format date for display (e.g., "Oct 1")
    const formatWeekLabel = (dateString) => {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };
    
    const maxCount = Math.max(...weeklyArray.map(d => d.count), 1);
    const chartHeight = 300;
    const chartWidth = "100%";
    const paddingBottom = 80;
    const paddingTop = 10;
    const paddingX = 20; // Padding to prevent dots from being cut off
    const svgHeight = chartHeight - paddingBottom - paddingTop;
    const svgWidth = 800; // Fixed width for SVG calculations
    
    return (
      <div style={{ position: "relative", width: chartWidth, height: chartHeight, paddingRight: 20, overflow: "visible" }}>
        {/* Y-axis label */}
        <div style={{ 
          position: "absolute", 
          right: -25, 
          top: "50%", 
          transform: "rotate(-90deg)",
          transformOrigin: "center",
          color: "#8ea0c6",
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap"
        }}>
          Sold Units
        </div>
        {/* Y-axis labels */}
        <div style={{ position: "absolute", left: 0, top: paddingTop, bottom: paddingBottom, width: 40 }}>
          {(() => {
            // Generate unique y-axis values with proper spacing
            const numTicks = 4;
            const values = [];
            
            // Always include 0 at the bottom
            values.push({ value: 0, index: numTicks });
            
            // Calculate step size to ensure unique values
            let step = maxCount / numTicks;
            
            // If step is less than 1, use integer steps
            if (step < 1) {
              step = 1;
            } else {
              // Round step to a nice number
              const magnitude = Math.pow(10, Math.floor(Math.log10(step)));
              step = Math.ceil(step / magnitude) * magnitude;
            }
            
            // Generate intermediate values
            for (let i = numTicks - 1; i > 0; i--) {
              const value = Math.round(step * i);
              if (value <= maxCount && value > 0) {
                values.push({ value, index: i });
              }
            }
            
            // Always include maxCount at the top
            if (maxCount > 0) {
              values.push({ value: maxCount, index: 0 });
            }
            
            // Remove duplicates and sort by index
            const uniqueValues = [];
            const seen = new Set();
            values.sort((a, b) => a.index - b.index);
            
            for (const item of values) {
              if (!seen.has(item.value)) {
                seen.add(item.value);
                uniqueValues.push(item);
              }
            }
            
            return uniqueValues.map(({ value, index }) => {
              // Calculate position based on value, not index
              const topPercent = maxCount > 0 ? ((maxCount - value) / maxCount) * 100 : 0;
              return (
                <div
                  key={`${value}-${index}`}
                  style={{
                    position: "absolute",
                    top: `${topPercent}%`,
                    transform: "translateY(-50%)",
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 5
                  }}
                >
                  <div style={{ color: "#8ea0c6", fontSize: 11, fontWeight: 600, textAlign: "right" }}>
                    {value}
                  </div>
                  <div style={{ width: 8, height: 1, background: "rgba(255, 255, 255, 0.3)" }} />
                </div>
              );
            });
          })()}
        </div>
        
        {/* Chart area */}
        <div style={{ marginLeft: 50, marginRight: 20, position: "relative", height: svgHeight, marginTop: paddingTop, overflow: "visible" }}>
          {/* Grid lines */}
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: `${(i / 4) * 100}%`,
                left: 0,
                right: 0,
                height: 1,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }}
            />
          ))}
          
          {/* Line chart */}
          <svg width="100%" height="100%" style={{ display: "block", overflow: "visible" }} viewBox={`${-paddingX} 0 ${svgWidth + (paddingX * 2)} ${svgHeight}`} preserveAspectRatio="none">
            <defs>
              <linearGradient id="demandGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#9333ea" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#9333ea" stopOpacity="0" />
              </linearGradient>
            </defs>
            
            {/* Area fill */}
            {weeklyArray.length > 0 && (() => {
              const dataWidth = weeklyArray.length > 1 ? weeklyArray.length - 1 : 1;
              const availableWidth = svgWidth - (paddingX * 2);
              const points = weeklyArray.map((d, i) => {
                const x = paddingX + (i / dataWidth) * availableWidth;
                const y = svgHeight - ((d.count / maxCount) * svgHeight);
                return { x, y };
              });
              
              const pathData = `M ${paddingX},${svgHeight} ${points.map(p => `L ${p.x},${p.y}`).join(' ')} L ${svgWidth - paddingX},${svgHeight} Z`;
              
              return (
                <path
                  d={pathData}
                  fill="url(#demandGradient)"
                />
              );
            })()}
            
            {/* Line */}
            {weeklyArray.length > 0 && (() => {
              const dataWidth = weeklyArray.length > 1 ? weeklyArray.length - 1 : 1;
              const availableWidth = svgWidth - (paddingX * 2);
              const points = weeklyArray.map((d, i) => {
                const x = paddingX + (i / dataWidth) * availableWidth;
                const y = svgHeight - ((d.count / maxCount) * svgHeight);
                return `${x},${y}`;
              }).join(' ');
              
              return (
                <polyline
                  points={points}
                  fill="none"
                  stroke="#9333ea"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              );
            })()}
            
            {/* Data points - aligned with x-axis values */}
            {weeklyArray.map((d, i) => {
              const dataWidth = weeklyArray.length > 1 ? weeklyArray.length - 1 : 1;
              const availableWidth = svgWidth - (paddingX * 2);
              // Align x position with the center of each week label
              const x = paddingX + (i / dataWidth) * availableWidth;
              const y = svgHeight - ((d.count / maxCount) * svgHeight);
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r="4"
                  fill="#9333ea"
                  stroke="#ffffff"
                  strokeWidth="2"
                />
              );
            })}
          </svg>
        </div>
        
        {/* X-axis labels */}
        <div style={{ marginLeft: 50, marginRight: 20, marginTop: 12, paddingTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", paddingRight: 10 }}>
            {weeklyArray.map((d, i) => {
              const isFirst = i === 0;
              const isLast = i === weeklyArray.length - 1;
              return (
                <div
                  key={i}
                  style={{
                    color: "#8ea0c6",
                    fontSize: 10,
                    fontWeight: 600,
                    transform: isFirst ? "translateX(0)" : isLast ? "translateX(0)" : "translateX(-50%)",
                    textAlign: isFirst ? "left" : isLast ? "right" : "center",
                    whiteSpace: "nowrap"
                  }}
                >
                  {formatWeekLabel(d.date)}
                </div>
              );
            })}
          </div>
          {/* X-axis label */}
          <div style={{ 
            textAlign: "center", 
            color: "#8ea0c6", 
            fontSize: 12, 
            fontWeight: 600,
            marginTop: 8
          }}>
            Week
          </div>
        </div>
      </div>
    );
  };

  const ScatterChart = () => {
    if (scatterData.length === 0) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 280, color: '#8ea0c6', fontSize: 14 }}>
          No data to display
        </div>
      );
    }

    // Calculate dynamic ranges from actual data
    const allPrices = scatterData.map(point => point.price);
    const allMileages = scatterData.map(point => point.mileage);
    
    let minPrice = Math.min(...allPrices);
    let maxPrice = Math.max(...allPrices);
    let minMileage = Math.min(...allMileages);
    let maxMileage = Math.max(...allMileages);
    
    // Add padding so points aren't flush against edges (5% of range, with minimum padding)
    const priceSpan = maxPrice - minPrice;
    const mileageSpan = maxMileage - minMileage;
    const pricePadding = Math.max(priceSpan * 0.05, 500);
    const mileagePadding = Math.max(mileageSpan * 0.05, 1000);
    
    // When all values are the same, use minimum padding so the point is centered
    const displayMinPrice = minPrice - (priceSpan > 0 ? pricePadding : 2500);
    const displayMaxPrice = maxPrice + (priceSpan > 0 ? pricePadding : 2500);
    const displayMinMileage = Math.max(0, minMileage - (mileageSpan > 0 ? mileagePadding : 5000));
    const displayMaxMileage = maxMileage + (mileageSpan > 0 ? mileagePadding : 5000);
    
    // Ensure we always have a positive range to avoid division by zero
    const safePriceRange = Math.max(displayMaxPrice - displayMinPrice, 1);
    const safeMileageRange = Math.max(displayMaxMileage - displayMinMileage, 1);
    
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', flex: 1, minHeight: 0 }}>
        {/* Y-axis labels and ticks */}
        <div style={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 50 }}>
          {(() => {
            const numTicks = 5;
            const ticks = [];
            for (let i = 0; i <= numTicks; i++) {
              const ratio = i / numTicks;
              const price = displayMinPrice + safePriceRange * (1 - ratio);
              const topPercent = (ratio * 100).toFixed(1);
              ticks.push(
                <div key={i} style={{ 
                  position: 'absolute', 
                  top: `${topPercent}%`, 
                  transform: 'translateY(-50%)', 
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5
                }}>
                  {/* Label */}
                  <div style={{
                    color: '#8ea0c6',
                    fontSize: 11,
                    fontWeight: 600,
                    textAlign: 'right',
                    lineHeight: 1
                  }}>
                    ${(price / 1000).toFixed(0)}k
                  </div>
                  {/* Tick mark - centered with label */}
                  <div style={{
                    width: 8,
                    height: 1,
                    background: 'rgba(255, 255, 255, 0.3)',
                    flexShrink: 0
                  }}></div>
                </div>
              );
            }
            return ticks;
          })()}
        </div>
        
        {/* Chart area */}
        <div 
          data-chart-container 
          style={{ marginLeft: 60, position: 'relative', height: '100%' }}
          onMouseLeave={() => {
            // Clear tooltip when mouse leaves chart area
            setHoveredPoint(null);
            setHoveredPosition({ x: 0, y: 0 });
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current);
              tooltipTimeoutRef.current = null;
            }
          }}
        >
          {/* Grid lines */}
          {(() => {
            const numGridLines = 4;
            const gridLines = [];
            for (let i = 1; i <= numGridLines; i++) {
              const topPercent = (i * 20).toFixed(1); // 20%, 40%, 60%, 80%
              gridLines.push(
                <div key={i} style={{
                  position: 'absolute',
                  top: `${topPercent}%`,
                  left: 0,
                  right: 0,
                  height: 1,
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}></div>
              );
            }
            return gridLines;
          })()}
          
          {/* Data points */}
          {scatterData.map((point, index) => {
            const x = ((point.mileage - displayMinMileage) / safeMileageRange) * 100;
            // Use percentage-based positioning - data fits between displayMin/Max with padding
            const priceRatio = (point.price - displayMinPrice) / safePriceRange;
            const y = 10 + (1 - priceRatio) * 80; // 10% top padding, 80% height for data area

            return (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: `${Math.max(0, Math.min(100, x))}%`,
                  top: `${Math.max(0, Math.min(100, y))}%`,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: point.type === 'sold' ? '#9333ea' : '#3b82f6',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  zIndex: 2
                }}
                onMouseEnter={(e) => {
                  // Clear any existing timeout
                  if (tooltipTimeoutRef.current) {
                    clearTimeout(tooltipTimeoutRef.current);
                    tooltipTimeoutRef.current = null;
                  }
                  
                  const rect = e.currentTarget.getBoundingClientRect();
                  const chartContainer = e.currentTarget.closest('[data-chart-container]');
                  const chartRect = chartContainer?.getBoundingClientRect();
                  if (chartRect && chartContainer) {
                    const containerWidth = chartContainer.offsetWidth;
                    const containerHeight = chartContainer.offsetHeight;
                    setHoveredPosition({
                      x: rect.left - chartRect.left + rect.width / 2,
                      y: rect.top - chartRect.top + rect.height / 2,
                      containerWidth,
                      containerHeight
                    });
                  }
                  setHoveredPoint({ ...point, index });
                }}
                onMouseLeave={() => {
                  // Clear tooltip immediately
                  setHoveredPoint(null);
                  setHoveredPosition({ x: 0, y: 0 });
                  // Clear any pending timeout
                  if (tooltipTimeoutRef.current) {
                    clearTimeout(tooltipTimeoutRef.current);
                    tooltipTimeoutRef.current = null;
                  }
                }}
              />
            );
          })}
          
          {/* Hover Tooltip */}
          {hoveredPoint && hoveredPosition.containerWidth && (
            <div 
              style={{
                position: 'absolute',
                left: `${Math.min(85, Math.max(0, (hoveredPosition.x / hoveredPosition.containerWidth) * 100))}%`,
                top: `${Math.min(75, Math.max(5, (hoveredPosition.y / hoveredPosition.containerHeight) * 100))}%`,
                transform: hoveredPosition.x > hoveredPosition.containerWidth / 2 
                  ? 'translate(-100%, -50%)' 
                  : 'translate(0, -50%)',
                marginLeft: hoveredPosition.x > hoveredPosition.containerWidth / 2 ? '-16px' : '16px',
                background: 'rgba(0, 0, 0, 0.95)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 12,
                padding: 16,
                minWidth: 280,
                maxWidth: 320,
                zIndex: 1000,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none'
              }}
            >
              {/* Car Image */}
              {hoveredPoint.image ? (
                <div style={{
                  width: '100%',
                  height: 160,
                  borderRadius: 8,
                  overflow: 'hidden',
                  marginBottom: 12,
                  background: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={hoveredPoint.image} 
                    alt={`${hoveredPoint.year} ${hoveredPoint.make} ${hoveredPoint.model}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div style="color: #8ea0c6; font-size: 12px;">No image available</div>';
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  width: '100%',
                  height: 160,
                  borderRadius: 8,
                  background: 'rgba(255, 255, 255, 0.05)',
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8ea0c6',
                  fontSize: 12
                }}>
                  No image available
                </div>
              )}
              
              {/* Vehicle Info */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                  {hoveredPoint.heading || `${hoveredPoint.year} ${hoveredPoint.make} ${hoveredPoint.model}`.trim()}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <div>
                    <div style={{ color: '#8ea0c6', fontSize: 11, marginBottom: 2 }}>Price</div>
                    <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                      ${hoveredPoint.price.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#8ea0c6', fontSize: 11, marginBottom: 2 }}>Mileage</div>
                    <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                      {hoveredPoint.mileage.toLocaleString()} mi
                    </div>
                  </div>
                </div>
              </div>
              
              {/* URL Link */}
              {hoveredPoint.url && (
                <a
                  href={hoveredPoint.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHoveredPoint(null);
                  }}
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                    color: '#ffffff',
                    textAlign: 'center',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    marginTop: 8,
                    pointerEvents: 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  View Listing →
                </a>
              )}
            </div>
          )}

          {/* X-axis labels and ticks */}
          <div style={{ position: 'absolute', bottom: -25, left: 0, right: 0, height: 25 }}>
            {(() => {
              const numTicks = 5;
              const ticks = [];
              for (let i = 0; i <= numTicks; i++) {
                const ratio = i / numTicks;
                const mileage = displayMinMileage + safeMileageRange * ratio;
                const leftPercent = (ratio * 100).toFixed(1);
                ticks.push(
                  <div key={i} style={{ position: 'absolute', left: `${leftPercent}%`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Tick mark */}
                    <div style={{
                      width: 1,
                      height: 5,
                      background: 'rgba(255, 255, 255, 0.3)',
                      marginBottom: 4
                    }}></div>
                    {/* Label */}
                    <div style={{
                      color: '#8ea0c6',
                      fontSize: 11,
                      fontWeight: 600,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      lineHeight: 1
                    }}>
                      {(mileage / 1000).toFixed(0)}k
                    </div>
                  </div>
                );
              }
              return ticks;
            })()}
          </div>
          
          {/* Legend/Label at bottom */}
          <div style={{ 
            position: 'absolute', 
            bottom: -50, 
            left: 0, 
            right: 0, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 16,
            paddingTop: 8
          }}>
            {chartFilter === 'Both' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#3b82f6'
                  }}></div>
                  <span style={{ color: '#8ea0c6', fontSize: 12, fontWeight: 500 }}>Active Listings</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#9333ea'
                  }}></div>
                  <span style={{ color: '#8ea0c6', fontSize: 12, fontWeight: 500 }}>Sold Listings</span>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: chartFilter === 'Active' ? '#3b82f6' : '#9333ea'
                }}></div>
                <span style={{ color: '#8ea0c6', fontSize: 12, fontWeight: 500 }}>
                  Showing {chartFilter} Listings
                </span>
              </div>
            )}
          </div>
          
        </div>
      </div>
    );
  };


  const DOMGauge = () => {
    const percentage = realData.avgDOM != null ? Math.min((realData.avgDOM / 200) * 100, 100) : 0; // Cap at 100%
    
    return (
      <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto' }}>
        {/* Outer glow ring */}
        <div style={{
          position: 'absolute',
          top: -4,
          left: -4,
          right: -4,
          bottom: -4,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(34, 211, 238, 0.3), rgba(34, 211, 238, 0.1))',
          filter: 'blur(8px)'
        }}></div>
        
        {/* Background circle */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
        }}></div>
        
        {/* Progress arc */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '50%',
          background: `conic-gradient(from -90deg, #22d3ee 0deg, #22d3ee ${percentage * 3.6}deg, transparent ${percentage * 3.6}deg)`,
          border: '1px solid transparent',
          filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.4))'
        }}></div>
        
        {/* Inner circle background */}
        <div style={{
          position: 'absolute',
          top: 20,
          left: 20,
          right: 20,
          bottom: 20,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)'
        }}></div>
        
        {/* Center value */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#ffffff',
          fontSize: 36,
          fontWeight: 'bold',
          textAlign: 'center',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          zIndex: 2
        }}>
          {realData.avgDOM != null ? realData.avgDOM : 'N/A'}
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      background: 'transparent', 
      minHeight: '100vh', 
      padding: '24px',
      color: '#ffffff'
    }}>
      <div style={{ display: 'grid', gap: 24 }}>
        
        {/* Top Row - Three Cards */}
        <Row gutter={24} justify="center">
          <Col xs={24} sm={10} md={8} lg={6}>
            <Card
              bordered={false}
              style={{
                background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
                border: "1px solid rgba(255,255,255,.06)",
                borderRadius: 14,
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
                height: 140
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ textAlign: 'center' }}>
                <Text style={{ color: '#ffffff', fontSize: 16, fontWeight: 600, display: 'block', marginBottom: 12 }}>
                  Active Listings
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <CarOutlined style={{ fontSize: 32, color: '#3b82f6' }} />
                  <Text style={{ color: '#ffffff', fontSize: 36, fontWeight: 'bold' }}>
                    {market_metrics?.active_count || 0}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={10} md={8} lg={6}>
            <Card
              bordered={false}
              style={{
                background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
                border: "1px solid rgba(255,255,255,.06)",
                borderRadius: 14,
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
                height: 140
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ textAlign: 'center' }}>
                <Text style={{ color: '#ffffff', fontSize: 16, fontWeight: 600, display: 'block', marginBottom: 12 }}>
                  Sold Listings
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <CarOutlined style={{ fontSize: 32, color: '#9333ea' }} />
                  <Text style={{ color: '#ffffff', fontSize: 36, fontWeight: 'bold' }}>
                    {market_metrics?.sold_count || 0}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={10} md={8} lg={6}>
            <Card
              bordered={false}
              style={{
                background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
                border: "1px solid rgba(255,255,255,.06)",
                borderRadius: 14,
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
                height: 140
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ textAlign: 'center' }}>
                <Text style={{ color: '#ffffff', fontSize: 16, fontWeight: 600, display: 'block', marginBottom: 12 }}>
                  Avg. DOM
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <ClockCircleOutlined style={{ fontSize: 32, color: '#a855f7' }} />
                  <Text style={{ color: '#ffffff', fontSize: 36, fontWeight: 'bold' }}>
                    {realData.avgDOM != null ? realData.avgDOM : 'N/A'}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* AI Recommendation and Price Movement Row */}
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card
              className="ai-recommendation-card"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '3px solid #ef4444',
                borderRadius: 12,
                position: 'relative',
                marginBottom: 0,
                height: 280,
                display: 'flex',
                flexDirection: 'column'
              }}
              bodyStyle={{ 
                padding: '12px 20px 12px 20px',
                marginBottom: 0,
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                marginBottom: 0, 
                paddingBottom: 0 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, width: '100%' }}>
                  <Title level={4} style={{ color: '#ffffff', margin: 0, marginBottom: 0 }}>
                    AI Recommendation
                  </Title>
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flex: 1
                }}>
                  <div style={{ 
                    color: '#8ea0c6', 
                    fontSize: 24, 
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    marginBottom: 8
                  }}>
                    Coming Soon
                  </div>
                  <div style={{ 
                    color: '#64748b', 
                    fontSize: 14, 
                    textAlign: 'center',
                    maxWidth: '80%'
                  }}>
                    AI-powered recommendations will be available shortly
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Price Movement in Market */}
        <Row gutter={24}>
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
                background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
                border: "1px solid rgba(255,255,255,.06)",
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
                display: 'flex',
                flexDirection: 'column'
              }}
              bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
            >
              <div style={{ color: "#ffffff", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                Price Movement in Market (Last 14 Days)
              </div>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ color: "#ffffff", fontSize: 32, fontWeight: 700 }}>
                    {(() => {
                      const recentSales = market_analysis?.recent_sales || [];
                      const priceDrops = recentSales.filter(sale => sale.price_change && sale.price_change < 0).length;
                      return priceDrops || 0;
                    })()}
                  </div>
                  <div style={{ color: "#8ea0c6", fontSize: 14 }}>
                    price drops
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Market Activity (Last 14 Days) - Full Width */}
        <Row gutter={24} justify="center">
          <Col xs={24} sm={24} md={24} lg={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
                background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
                border: "1px solid rgba(255,255,255,.06)",
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
              }}
              bodyStyle={{ padding: 16 }}
            >
              <div style={{ color: "#ffffff", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                Market Activity (Last 14 Days)
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ color: "#8ea0c6", fontSize: 12, marginBottom: 8 }}>New Active Listings</div>
                  <div style={{ color: "#ffffff", fontSize: 24, fontWeight: 700 }}>
                    {newActiveListings}
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ color: "#8ea0c6", fontSize: 12, marginBottom: 8 }}>New Sold Listings</div>
                  <div style={{ color: "#ffffff", fontSize: 24, fontWeight: 700 }}>
                    {newSoldListings}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "center", marginTop: 12 }}>
                {(() => {
                  let status, color;
                  if (newSoldListings > newActiveListings) {
                    status = "Increasing";
                    color = "#22c55e";
                  } else if (newSoldListings === newActiveListings) {
                    status = "Balanced";
                    color = "#f59e0b";
                  } else {
                    status = "Decreasing";
                    color = "#ef4444";
                  }
                  return (
                    <div style={{ color, fontSize: 12, fontWeight: 600 }}>
                      {status}
                    </div>
                  );
                })()}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Bottom Row - Price vs. Mileage - Hidden on mobile */}
        {!isMobile && (
          <Row gutter={0}>
            <Col xs={24}>
              <Card
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 12,
                  height: 450,
                  display: 'flex',
                  flexDirection: 'column'
                }}
                bodyStyle={{ 
                  padding: '20px 20px 60px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
                  <Title level={4} style={{ color: '#ffffff', margin: 0 }}>
                    Price vs. Mileage
                  </Title>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Button
                      ref={similarMileageButtonRef}
                      type="default"
                      size="small"
                      className={showSimilarMileageOnly ? 'similar-mileage-active' : 'similar-mileage-inactive'}
                      onClick={() => setShowSimilarMileageOnly(!showSimilarMileageOnly)}
                      style={{
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '6px 12px',
                        height: 'auto',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!showSimilarMileageOnly) {
                          e.currentTarget.style.setProperty('background', 'rgba(255, 255, 255, 0.1)', 'important');
                          e.currentTarget.style.setProperty('border-color', 'rgba(255, 255, 255, 0.2)', 'important');
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showSimilarMileageOnly) {
                          e.currentTarget.style.setProperty('background', 'rgba(255, 255, 255, 0.05)', 'important');
                          e.currentTarget.style.setProperty('border-color', 'rgba(255, 255, 255, 0.1)', 'important');
                        } else {
                          // Restore active state on mouse leave
                          e.currentTarget.style.setProperty('background', 'linear-gradient(to right, #a855f7 0%, #22c55e 100%)', 'important');
                          e.currentTarget.style.setProperty('border', '1px solid #3b82f6', 'important');
                          e.currentTarget.style.setProperty('border-color', '#3b82f6', 'important');
                        }
                      }}
                    >
                      Use similar mileage (±10%)
                    </Button>
                    {vehicleVersion && (
                      <Button
                        size="small"
                        type={isVersionMatchOnly ? "primary" : "default"}
                        onClick={() => setIsVersionMatchOnly(!isVersionMatchOnly)}
                        className={isVersionMatchOnly ? "similar-mileage-selected filter-btn" : "filter-btn"}
                        style={{
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '6px 12px',
                          height: 'auto',
                        }}
                      >
                        {isVersionMatchOnly 
                          ? `✓ Show Matching Version Only` 
                          : `Show Matching Version Only`}
                      </Button>
                    )}
                    <Segmented
                    options={['Active', 'Sold', 'Both']}
                    value={chartFilter}
                    onChange={setChartFilter}
                    className={`chart-filter-segmented chart-filter-${chartFilter.toLowerCase()}`}
                    style={{
                      backgroundColor: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '4px',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(12px)',
                      gap: '4px'
                    }}
                    styles={{
                      item: {
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: '500',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: 'none',
                        background: 'transparent',
                        position: 'relative',
                        minWidth: '70px',
                        textAlign: 'center'
                      },
                      itemSelected: {
                        color: '#ffffff',
                        fontWeight: '600',
                        transform: 'scale(1.02)',
                        letterSpacing: '0.3px'
                      },
                      thumb: {
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }
                    }}
                  />
                  </div>
                </div>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <ScatterChart />
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Demand Chart: Weekly Sold Count - Hidden on mobile */}
        {!isMobile && (
          <Row gutter={24} justify="center">
            <Col xs={24} sm={24} md={24} lg={24}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
                  border: "1px solid rgba(255,255,255,.06)",
                  boxShadow: "0 10px 26px rgba(0,0,0,.35)",
                }}
                bodyStyle={{ padding: 24 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <div style={{ color: "#ffffff", fontSize: 16, fontWeight: 600 }}>
                    Demand Chart
                  </div>
                  {vehicleVersion && (
                    <Button
                      size="small"
                      type={isVersionMatchOnly ? "primary" : "default"}
                      onClick={() => setIsVersionMatchOnly(!isVersionMatchOnly)}
                      className={isVersionMatchOnly ? "similar-mileage-selected filter-btn" : "filter-btn"}
                    >
                      {isVersionMatchOnly 
                        ? `✓ Show ${vehicleVersion} Match Only` 
                        : `Show ${vehicleVersion} Match Only`}
                    </Button>
                  )}
                </div>
                <DemandChart data={data} isVersionMatchOnly={isVersionMatchOnly} vehicleVersion={vehicleVersion} />
              </Card>
            </Col>
          </Row>
        )}
        
      </div>
      <style>{`
        .ai-recommendation-card {
          height: 280px !important;
          min-height: 280px !important;
        }
        .ai-recommendation-card .ant-card-body {
          padding-bottom: 12px !important;
          padding-top: 12px !important;
          height: 100% !important;
          min-height: 100% !important;
          flex-shrink: 0 !important;
        }
        .ai-recommendation-card .ant-typography {
          margin-bottom: 0 !important;
        }
        .ai-recommendation-card h4.ant-typography {
          margin-bottom: 0 !important;
        }
        /* Enhanced Chart Filter Tabs */
        .chart-filter-segmented .ant-segmented-item {
          position: relative;
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item * {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item-label {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item span {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item:hover:not(.ant-segmented-item-selected) {
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.05) !important;
        }
        .chart-filter-segmented .ant-segmented-item:hover:not(.ant-segmented-item-selected) * {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item:hover:not(.ant-segmented-item-selected) .ant-segmented-item-label {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item-selected {
          position: relative;
        }
        .chart-filter-segmented .ant-segmented-item-selected * {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item-selected .ant-segmented-item-label {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item-selected span {
          color: #ffffff !important;
        }
        .chart-filter-segmented .ant-segmented-item-selected::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 8px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
          pointer-events: none;
        }
        /* Active tab gradient */
        .chart-filter-active .ant-segmented-item-selected {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
        }
        /* Sold tab gradient */
        .chart-filter-sold .ant-segmented-item-selected {
          background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%) !important;
          box-shadow: 0 4px 16px rgba(147, 51, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
        }
        /* Both tab gradient */
        .chart-filter-both .ant-segmented-item-selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
        }
        /* Similar Mileage Button Overrides */
        .similar-mileage-active.ant-btn,
        .similar-mileage-active.ant-btn-default,
        .similar-mileage-active.ant-btn-primary {
          background: linear-gradient(to right, #a855f7 0%, #22c55e 100%) !important;
          border: 1px solid #3b82f6 !important;
          border-color: #3b82f6 !important;
          color: #ffffff !important;
        }
        .similar-mileage-active.ant-btn:hover,
        .similar-mileage-active.ant-btn:focus,
        .similar-mileage-active.ant-btn:active {
          background: linear-gradient(to right, #a855f7 0%, #22c55e 100%) !important;
          border: 1px solid #3b82f6 !important;
          border-color: #3b82f6 !important;
          color: #ffffff !important;
        }
        .similar-mileage-inactive.ant-btn,
        .similar-mileage-inactive.ant-btn-default {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.1) !important;
          color: #ffffff !important;
        }
        .similar-mileage-inactive.ant-btn:hover {
          background: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
        }
      `}</style>
    </div>
  );
};

export default Overview;
